import { useEffect, useState } from 'react';
import { useAccount, useSignMessage, useDisconnect } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { SiweMessage } from 'siwe';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import LoadingSpinner from './LoadingSpinner';

interface WalletConnectProps {
  onSuccess?: () => void;
  onNewUser?: (registrationToken: string, walletAddress: string) => void;
}

export default function WalletConnect({ onSuccess, onNewUser }: WalletConnectProps) {
  const { address, isConnected, chainId } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();
  const { setAuth, setError } = useAuthStore();

  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  useEffect(() => {
    if (isConnected && address && !isVerifying) {
      handleVerification();
    }
  }, [isConnected, address]);

  const handleVerification = async () => {
    if (!address || !chainId) return;

    setIsVerifying(true);
    setVerificationError(null);
    console.log('[WalletConnect] Starting verification for address:', address, 'chainId:', chainId);

    try {
      // Get nonce from server
      console.log('[WalletConnect] Fetching nonce...');
      const { nonce } = await api.getWalletNonce(address);
      console.log('[WalletConnect] Got nonce:', nonce);

      // Create SIWE message
      const message = new SiweMessage({
        domain: window.location.host,
        address,
        statement: 'Sign in to LazaiTrader',
        uri: window.location.origin,
        version: '1',
        chainId,
        nonce,
      });

      const messageToSign = message.prepareMessage();
      console.log('[WalletConnect] SIWE message created for domain:', window.location.host);

      // Sign the message
      console.log('[WalletConnect] Requesting signature...');
      const signature = await signMessageAsync({ message: messageToSign });
      console.log('[WalletConnect] Got signature');

      // Verify with backend
      console.log('[WalletConnect] Verifying with backend...');
      const result = await api.verifyWalletSignature(address, messageToSign, signature);
      console.log('[WalletConnect] Backend response:', JSON.stringify(result, null, 2));

      if (result.authenticated && result.sessionToken && result.user) {
        // Existing user - logged in
        console.log('[WalletConnect] Login successful, setting auth...');
        setAuth(result.user, result.sessionToken);
        onSuccess?.();
      } else if (result.isNewUser && result.registrationToken) {
        // New user - needs registration
        console.log('[WalletConnect] New user, redirecting to registration...');
        onNewUser?.(result.registrationToken, address);
      } else if (result.existingWallet) {
        console.log('[WalletConnect] Wallet conflict detected');
        setVerificationError(
          `This wallet is already registered to another account. Please use a different wallet.`
        );
        disconnect();
      } else {
        // Unexpected response - show error and disconnect
        console.error('[WalletConnect] Unexpected response:', result);
        setVerificationError('Login failed. Please try again.');
        disconnect();
      }
    } catch (error) {
      console.error('[WalletConnect] Verification failed:', error);
      const message = error instanceof Error ? error.message : 'Verification failed';
      console.error('[WalletConnect] Error message:', message);
      setVerificationError(message);
      setError(message);
      disconnect();
    } finally {
      setIsVerifying(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="flex flex-col items-center space-y-4">
        <LoadingSpinner size="lg" />
        <p className="text-dark-300">Verifying wallet...</p>
        <p className="text-sm text-dark-400">Please sign the message in your wallet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {verificationError && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200 text-sm">
          {verificationError}
        </div>
      )}

      <ConnectButton.Custom>
        {({ openConnectModal, mounted }) => {
          const ready = mounted;

          return (
            <div
              {...(!ready && {
                'aria-hidden': true,
                style: {
                  opacity: 0,
                  pointerEvents: 'none',
                  userSelect: 'none',
                },
              })}
            >
              <button
                onClick={openConnectModal}
                className="w-full btn bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white py-3 text-lg font-medium flex items-center justify-center space-x-2"
              >
                <WalletIcon className="w-5 h-5" />
                <span>Connect Wallet</span>
              </button>
            </div>
          );
        }}
      </ConnectButton.Custom>

      <p className="text-xs text-dark-400 text-center">
        Connect with MetaMask, WalletConnect, Coinbase Wallet, or other supported wallets
      </p>
    </div>
  );
}

function WalletIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}
